import {
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  PresentationWindow,
  PlaylistWindowManagerState,
  Presentation as WindowManagerPresentation,
} from '@raydiant/playlist-window-manager';
import IFrameRenderer from '@raydiant/playlist-window-manager/build/IFrameRenderer';
import * as RE from '@raydiant/playlist-rule-engine';
import {
  mapPresentationToV2,
  PlaylistPlaybackContent,
  Presentation,
} from '@raydiant/api-client-js';
import apiClient from '../../clients/miraClient';
import deepEqual from 'fast-deep-equal';
import * as A from '../../clients/mira/types/Application';
import * as P from '../../clients/mira/types/Presentation';
import * as T from '../../clients/mira/types/Theme';
import {
  collectApplicationVariables,
  hasPresentationChanged,
  hasThemeChanged,
} from '../../utilities';
import { injectPresentationUploads } from '../../utilities/presentationUtils';
import combinePlaybackContent from './combinePlaybackContent';
import logger from '../../logger';
import { BuilderState, PresentationUpload } from '../../types';
import useApplicationVersionToken from '../../hooks/useApplicationVersionToken';

interface PresentationLoaderProps {
  presentation: Presentation;
  presentationUploads: PresentationUpload[];
  appVersion: A.ApplicationVersion;
  theme?: T.Theme;
  builderState?: BuilderState;
  onPresentationProperties?: (
    properties: P.PresentationProperty[],
    strings: Record<string, string>,
  ) => void;
}

export interface PresentationLoaderHandle {
  createObjectURL: (blob: Blob) => Promise<string>;
}

const PresentationLoader: ForwardRefRenderFunction<
  PresentationLoaderHandle,
  PresentationLoaderProps
> = (
  {
    presentation,
    presentationUploads,
    appVersion,
    theme,
    builderState,
    onPresentationProperties,
  },
  forwardedRef,
) => {
  const windowRef = useRef<PresentationWindow | null>(null);
  const prevPresentationRef = useRef<P.Presentation | null>(null);
  const prevAppVersionRef = useRef<A.ApplicationVersion | null>(null);
  const prevThemeRef = useRef<T.Theme | undefined | null>(null);
  const prevBuilderStateRef = useRef<BuilderState | undefined | null>(null);
  const prevAppVersionTokenRef = useRef<string | undefined | null>(null);

  useImperativeHandle(forwardedRef, () => {
    return {
      async createObjectURL(blob) {
        if (windowRef.current) {
          return windowRef.current.createObjectURL(blob);
        }
        return '';
      },
    };
  });

  const playbackContentCacheRef = useRef<{
    [playlistId: string]: PlaylistPlaybackContent;
  }>({});
  const rendererRef = useRef<IFrameRenderer | null>(null);

  // Generate a temporary token if the embedded URL format contains the special :$token
  // input parameter.
  const hasTokenInUrl = appVersion?.embeddedUrlFormat?.includes(':$token');
  const { data: appVersionToken } = useApplicationVersionToken(
    hasTokenInUrl ? appVersion.id : undefined,
  );

  // Create the renderer when the container has been added to the DOM.
  const createRenderer = useCallback((node) => {
    if (node) {
      rendererRef.current = new IFrameRenderer(node);
    }
  }, []);

  // Remove the presentation window when unmounting.
  useEffect(() => {
    return () => {
      if (windowRef.current) {
        windowRef.current.remove();
      }

      if (rendererRef.current) {
        rendererRef.current.destroy();
      }
    };
  }, []);

  // Create window manager and rule enegine state from playback content.
  const getPresentationWindowState = useCallback(async () => {
    const playlistAppVars = collectApplicationVariables(
      'playlist',
      presentation,
      appVersion,
    );

    const allPlaybackContent = await Promise.all(
      playlistAppVars.map(async ({ applicationVariable: playlistId }) => {
        if (!playlistId) return {};

        // Cache playback content per playlist for the lifetime of the component.
        let playbackContent: PlaylistPlaybackContent =
          playbackContentCacheRef.current[playlistId];

        if (!playbackContent) {
          playbackContent = await apiClient.getPlaylistPlaybackContent(
            playlistId,
          );
          playbackContentCacheRef.current[playlistId] = playbackContent;
        }

        return playbackContent;
      }),
    );

    const {
      presentations = {},
      playlists = {},
      applicationVersions = {},
      applications = {},
      themes = {},
    } = combinePlaybackContent(allPlaybackContent);

    const ruleEngineState = new RE.State(
      RE.mapPlaylistsFromAPI(playlists),
      RE.mapPresentationsFromAPI(
        presentations,
        applicationVersions,
        applications,
      ),
      {},
    );

    const playlistWindowManagerState = new PlaylistWindowManagerState({
      presentations,
      applicationVersions,
      themes,
      publishedAt: '',
    });

    return { ruleEngineState, playlistWindowManagerState };
  }, [presentation, appVersion]);

  // Load the presentation window and run getProperties.
  const loadPresentationWindow = useCallback(async () => {
    if (!rendererRef.current) return;

    const { ruleEngineState, playlistWindowManagerState } =
      await getPresentationWindowState();

    let wmPresentation: WindowManagerPresentation = mapPresentationToV2(
      injectPresentationUploads(presentation, presentationUploads),
    );
    // Inject token into presentation.
    wmPresentation.token = appVersionToken;

    // Don't load the window if the app's URL depends on token and we don't have a token yet.
    if (hasTokenInUrl && !appVersionToken) {
      return;
    }

    const presentationWindow = new PresentationWindow({
      isPreview: true,
      windowId: 'preview',
      builderState,
      presentation: wmPresentation,
      applicationVersion: appVersion,
      theme,
      playlistWindowManagerState,
      ruleEngineState,
      renderer: rendererRef.current,
      logger: {
        debug: () => {},
        info: () => {},
        warn: () => {},
        error: (err) => {
          logger.warn(`Error from app preview: ${err}`);
        },
      },
      backgroundColor: 'black',
      appRendererUrl: `${window.location.protocol}//${window.location.host}/app-renderer/3.0.0`,
      onPresentationProperties,
      publishedAt: '',
      workerState: {},
    });

    windowRef.current = presentationWindow;

    try {
      await presentationWindow.load();

      // Avoid race condition issue with loadPresentationWindow being invoked twice, first with
      // an empty builderState and later with builderState.
      // See https://github.com/mirainc/mira-dash/pull/1253 for more info.
      if (windowRef.current !== presentationWindow) return;

      presentationWindow.show(false);

      // Play presentation.
      presentationWindow.play();
      // Evaluate conditional controls after loading.
      presentationWindow.runGetProperties(builderState);
    } catch (err) {
      console.error(`Error loading app preview: ${err}`);
    }
  }, [
    appVersion,
    builderState,
    presentation,
    presentationUploads,
    appVersionToken,
    theme,
    hasTokenInUrl,
    onPresentationProperties,
    getPresentationWindowState,
  ]);

  // Update presentation window
  const updatePresentationWindow = useCallback(async () => {
    const presentationWindow = windowRef.current;
    if (!presentationWindow) return;

    const { ruleEngineState, playlistWindowManagerState } =
      await getPresentationWindowState();

    let wmPresentation: WindowManagerPresentation = mapPresentationToV2(
      injectPresentationUploads(presentation, presentationUploads),
    );
    // Inject token into presentation.
    wmPresentation.token = appVersionToken;

    presentationWindow.setPresentation(
      wmPresentation,
      playlistWindowManagerState,
      ruleEngineState,
    );
    presentationWindow.setTheme(theme);
    presentationWindow.setBuilderState(builderState);
    presentationWindow.reload();

    // Evaluate conditional controls after updating.
    presentationWindow.runGetProperties(builderState);
  }, [
    presentation,
    presentationUploads,
    appVersionToken,
    theme,
    getPresentationWindowState,
    builderState,
  ]);

  // Create or update the presentation window.
  useEffect(() => {
    if (!rendererRef.current) return;

    if (windowRef.current) {
      // Update presentation window.
      const presentationWindow = windowRef.current;

      const didPresentationChange =
        prevPresentationRef.current !== null
          ? hasPresentationChanged(
              presentation,
              prevPresentationRef.current,
              appVersion,
            )
          : false;

      const didAppVersionChange =
        prevAppVersionRef.current !== null
          ? prevAppVersionRef.current.id !== appVersion.id
          : false;

      const didThemeChange =
        prevThemeRef.current !== null && prevThemeRef.current && theme
          ? hasThemeChanged(prevThemeRef.current, theme)
          : false;

      const didBuilderStateChange =
        prevBuilderStateRef.current !== null
          ? !deepEqual(prevBuilderStateRef.current, builderState)
          : false;

      const didAppVersionTokenChange =
        prevAppVersionTokenRef.current !== null &&
        prevAppVersionTokenRef.current !== appVersionToken;

      // Update the presentation window if the presentation, app version or theme
      // changes otherwise create a new presentation window.
      if (
        didPresentationChange ||
        didAppVersionChange ||
        didThemeChange ||
        didBuilderStateChange ||
        didAppVersionTokenChange
      ) {
        // Recreate the presentation window if the app version changed, otherwise rerender
        // the preview with the updated presentation and theme.
        if (didAppVersionChange) {
          presentationWindow.remove();
          loadPresentationWindow();
        } else if (
          didPresentationChange ||
          didThemeChange ||
          didBuilderStateChange ||
          didAppVersionTokenChange
        ) {
          // Rerender presentation window with updated presentation, state, theme and builder state.
          updatePresentationWindow();
        }
      }
    } else {
      // Create and load the presentation window.
      loadPresentationWindow();
    }

    // Set previous refs.
    prevPresentationRef.current = presentation;
    prevAppVersionRef.current = appVersion;
    prevThemeRef.current = theme;
    prevBuilderStateRef.current = builderState;
    prevAppVersionTokenRef.current = appVersionToken;
  }, [
    presentation,
    appVersion,
    appVersionToken,
    theme,
    builderState,
    loadPresentationWindow,
    updatePresentationWindow,
  ]);

  return <div ref={createRenderer} />;
};

export default forwardRef(PresentationLoader);
